import * as PIXI from "pixi.js";
import { symbolData } from "../../../SymbolData";
import { Settings } from "../../../settings";

// Free spin uses: 1 top buffer + N visible + 1 bottom buffer
const VISIBLE_SYMBOL_COUNT = Settings.SYMBOLS_VISIBLE;
const SPRITE_COUNT = VISIBLE_SYMBOL_COUNT + 2; // 5 total (1 + 3 + 1)
const VISIBLE_START_INDEX = 1; // Visible symbols start after the top buffer

// Cache available symbol IDs once (module-level constant)
const AVAILABLE_SYMBOL_IDS = (() => {
  return Object.entries(symbolData)
    .filter(([, data]) => data?.texture && data.texture !== null)
    .map(([id]) => parseInt(id, 10));
})();

/**
 * FreeSpinReelController
 * Lightweight reel controller for free spins — no spin animation.
 * Symbols are rendered instantly when API data arrives.
 * Creates PIXI sprites programmatically (imperative, not React components).
 */
class FreeSpinReelController {
  constructor(
    symbolsContainer,
    spriteSheet,
    reelData,
    reelIndex,
    symbolWidth,
    symbolHeight,
  ) {
    this.symbolsContainer = symbolsContainer;
    this.reelIndex = reelIndex;
    this.spritesheet = spriteSheet;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reels = reelData || [];
    this.spinningSprites = [];
    this.visibleSprites = [];

    // Scale cache
    this._scaleCache = null;
    this._lastCacheHeight = null;

    this._createSprites();
  }

  // --- Random symbol picking ---

  _pickRandomSymbolId() {
    if (!AVAILABLE_SYMBOL_IDS.length) return 1;
    return AVAILABLE_SYMBOL_IDS[
      Math.floor(Math.random() * AVAILABLE_SYMBOL_IDS.length)
    ];
  }

  // --- Sprite creation ---

  _createSprites() {
    if (!this.symbolsContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const sprite = new PIXI.Sprite();
      sprite.anchor.set(0.5);
      sprite.name = "free_spin_symbol_texture";
      sprite.alpha = 1;
      sprite.roundPixels = true;
      symbolContainer.addChild(sprite);

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite: sprite,
        currentSymbolId: null,
      });

      this.symbolsContainer.addChild(symbolContainer);
    }

    // Set initial textures
    this._applySymbols();

    // Cache visible sprites reference
    this.visibleSprites = this.spinningSprites.slice(
      VISIBLE_START_INDEX,
      VISIBLE_START_INDEX + VISIBLE_SYMBOL_COUNT,
    );
  }

  // --- Symbol scale (matches ReelControllerV2 logic exactly) ---

  _calculateSymbolScale() {
    if (!this.symbolHeight) return 1;

    // Cache — scale only depends on symbolHeight
    if (this._lastCacheHeight === this.symbolHeight && this._scaleCache !== null) {
      return this._scaleCache;
    }

    // Uniform scale: all symbols relative to standard height (168px)
    this._scaleCache = this.symbolHeight / Settings.SYMBOL_HEIGHT;
    this._lastCacheHeight = this.symbolHeight;
    return this._scaleCache;
  }

  // --- Texture setting ---

  _setSpriteTexture(spriteData, symbolId) {
    if (spriteData.currentSymbolId === symbolId) return;

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const textureName = symbolInfo.texture;

    if (!textureName) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet) {
      const texture =
        this.spritesheet.textures?.[`${textureName}.png`] || PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = 1;
    }

    // Apply uniform scale
    const scale = this._calculateSymbolScale();
    spriteData.normalSprite.scale.set(scale);
  }

  // --- Apply symbols: visible from API, buffers random ---

  _applySymbols() {
    if (!this.spinningSprites.length) return;

    const hasReelData =
      this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      let symbolId;

      if (
        hasReelData &&
        i >= VISIBLE_START_INDEX &&
        i < VISIBLE_START_INDEX + VISIBLE_SYMBOL_COUNT
      ) {
        // Visible symbol — use API data
        const reelIndex = i - VISIBLE_START_INDEX;
        symbolId = this.reels[reelIndex];
      } else {
        // Buffer symbol — random
        symbolId = this._pickRandomSymbolId();
      }

      this._setSpriteTexture(this.spinningSprites[i], symbolId);
    }
  }

  // --- Public API ---

  /**
   * Update reel with new API data and instantly render
   */
  updateReel(newReelData) {
    if (newReelData && Array.isArray(newReelData)) {
      this.reels = newReelData;
      this._applySymbols();
    }
  }

  /**
   * Resize all sprites to new dimensions
   */
  resizeSymbol(newWidth, newHeight) {
    if (newWidth === this.symbolWidth && newHeight === this.symbolHeight) return;

    this.symbolWidth = newWidth;
    this.symbolHeight = newHeight;

    // Invalidate scale cache
    this._scaleCache = null;
    this._lastCacheHeight = null;

    // Reposition and rescale all sprites
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      spriteData.container.y = i * this.symbolHeight;

      if (spriteData.currentSymbolId !== null) {
        const scale = this._calculateSymbolScale();
        spriteData.normalSprite.scale.set(scale);
      }
    }
  }

  /**
   * Get symbol container at specific index
   */
  getSymbolAtIndex(index) {
    if (index < 0 || index >= this.spinningSprites.length) return null;
    return this.spinningSprites[index];
  }

  /**
   * Get visible symbol at row index (0, 1, 2)
   */
  getVisibleSymbolAtRow(row) {
    return this.getSymbolAtIndex(VISIBLE_START_INDEX + row);
  }

  /**
   * Show winning symbol (hide texture for overlay spine to show through)
   */
  showWinningSymbol(index) {
    const spriteData = this.getVisibleSymbolAtRow(index);
    if (spriteData) {
      spriteData.normalSprite.alpha = 0.001;
    }
  }

  /**
   * Hide winning symbol (restore texture)
   */
  hideWinningSymbol(index) {
    const spriteData = this.getVisibleSymbolAtRow(index);
    if (spriteData) {
      spriteData.normalSprite.alpha = 1;
    }
  }

  /**
   * Restore all symbol textures to visible
   */
  clearWinningSymbols() {
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (spriteData.currentSymbolId !== null) {
        spriteData.normalSprite.alpha = 1;
      }
    }
  }

  /**
   * Remove all visual effects (restore all alphas)
   */
  removeVisualEffects() {
    this.clearWinningSymbols();
  }

  /**
   * Hide a specific symbol's texture (for landing animation overlays)
   */
  hideSymbolTexture(symbolIndex) {
    const spriteData = this.getVisibleSymbolAtRow(symbolIndex);
    if (spriteData) {
      spriteData.normalSprite.alpha = 0.001;
    }
  }

  /**
   * Show a specific symbol's texture (restore from hidden)
   */
  showSymbolTexture(symbolIndex) {
    const spriteData = this.getVisibleSymbolAtRow(symbolIndex);
    if (spriteData) {
      spriteData.normalSprite.alpha = 1;
    }
  }

  /**
   * Replace visible symbol at position with a new symbol ID
   */
  replaceSymbolAtPosition(symbolIndex, targetSymbolId) {
    const spriteData = this.getVisibleSymbolAtRow(symbolIndex);
    if (spriteData) {
      this._setSpriteTexture(spriteData, targetSymbolId);
    }
  }

  /**
   * Replace all visible symbols with a target symbol ID
   */
  replaceVisibleSymbols(targetSymbolId) {
    for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
      this.replaceSymbolAtPosition(i, targetSymbolId);
    }
  }

  /**
   * Cleanup — destroy all sprites
   */
  dispose() {
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      try {
        if (spriteData.container?.parent) {
          spriteData.container.parent.removeChild(spriteData.container);
        }
        if (spriteData.normalSprite && !spriteData.normalSprite.destroyed) {
          spriteData.normalSprite.destroy();
        }
        if (spriteData.container && !spriteData.container.destroyed) {
          spriteData.container.destroy();
        }
      } catch {
        // Already destroyed
      }
    }

    this.spinningSprites = [];
    this.visibleSprites = [];
    this.symbolsContainer = null;
    this.spritesheet = null;
    this.reels = null;
  }
}

export default FreeSpinReelController;
